/**
 * Avatar URL utilities
 * 
 * Handles proper formatting of avatar URLs, checking if they are full URLs
 * or relative paths that need to be prepended with the API server URL.
 */

import { apiConfig } from '@/config/api';

/**
 * Formats an avatar URL to ensure it's a complete, accessible URL
 * @param photoURL - The photo URL from user.photoURL or similar source
 * @returns A properly formatted avatar URL or null if no URL provided
 */
export function formatAvatarUrl(photoURL: string | null | undefined): string | null {
  if (!photoURL) return null;
  
  // If it's already a full URL (starts with http or https), use it as is
  if (photoURL.startsWith('http://') || photoURL.startsWith('https://')) {
    return photoURL;
  }
  
  // If it's a relative path (like /public/avatar/uuid or /avatar/uuid), 
  // prepend with API server URL
  if (photoURL.startsWith('/')) {
    // Remove leading slash to avoid double slashes
    const cleanPath = photoURL.substring(1);
    return `${apiConfig.baseUrl}/${cleanPath}`;
  }
  
  // If it's just a path without leading slash, also prepend API server URL
  return `${apiConfig.baseUrl}/${photoURL}`;
}

/**
 * Gets the avatar URL with fallback to default avatar
 * @param photoURL - The photo URL from user.photoURL or similar source
 * @param fallbackUrl - Fallback URL to use if no photoURL (defaults to '/default-avatar.png')
 * @returns A properly formatted avatar URL
 */
export function getAvatarUrlWithFallback(
  photoURL: string | null | undefined, 
  fallbackUrl: string = '/default-avatar.png'
): string {
  const formattedUrl = formatAvatarUrl(photoURL);
  return formattedUrl || fallbackUrl;
}
